import classNames from 'classnames'
import * as React from 'react'

import {getSingleBuildArg} from '../../../../../rest/builds'
import {getBuildLocator} from '../../../../../rest/locators'
import {isBuildLoaded} from '../../../../../selectors'
import {restApi} from '../../../../../services/rest'
import type {BuildId} from '../../../../../types'
import {noop} from '../../../../../utils/empty'
import BuildPlaceholder from '../../../../common/Builds/Build/Build.placeholder'
import BuildDetails from '../../../../common/Builds/BuildDetails/BuildDetails'
import {BuildsWrapper} from '../../../../common/Builds/Builds.wrapped'
import {useVisibilityRef} from '../../../../common/Visible/Visible.hooks'
import {EXPAND_AGENTS_OVERVIEW_STATE_KEY} from '../../AgentsPages.effects'
import {useStoredAgentsTreeBranchState} from '../../AgentsPages.hooks'
import type {AgentsTreeAgentTypeNode} from '../../AgentsSidebar/AgentsSidebar.types'
import {AgentTreeItemType} from '../../AgentsSidebar/AgentsSidebar.types'
import CloudAgentListView from '../AgentListView/CloudAgentListView'

import styles from './CloudAgentRow.css'

import {useAppSelector} from 'src/hooks/react-redux'

type Props = AgentsTreeAgentTypeNode & {
  className?: string | null | undefined
  removable?: boolean
}

function VisibleBuild({buildId}: {buildId: BuildId}) {
  const [visibilityRef, isVisible] = useVisibilityRef(String(buildId))
  const isLoaded = useAppSelector(state => isBuildLoaded(state, buildId))
  const [fetchSingleBuildData] =
    restApi.endpoints.getBuildNormalizedAsList.useLazyQuerySubscription()
  React.useEffect(() => {
    if (isVisible) {
      const result = fetchSingleBuildData(
        getSingleBuildArg(getBuildLocator(buildId), {
          withQueuedInfo: false,
        }),
        true,
      )
      return () => {
        result.abort()
      }
    }

    return noop
  }, [buildId, isVisible, fetchSingleBuildData])

  return (
    <>
      {isLoaded && isVisible ? (
        <BuildDetails
          ref={visibilityRef}
          visible={isVisible}
          wrapped
          buildId={buildId}
          alwaysShowLineBorders
        />
      ) : (
        <BuildPlaceholder outerRef={visibilityRef} />
      )}
    </>
  )
}

function CloudAgentRow({className, removable, ...agentTypeNode}: Props) {
  const [expanded, onClick] = useStoredAgentsTreeBranchState(
    EXPAND_AGENTS_OVERVIEW_STATE_KEY,
    AgentTreeItemType.AGENT_TYPE,
    agentTypeNode.id,
  )
  const buildIds =
    agentTypeNode.busyCount > 0
      ? agentTypeNode.children.reduce((result: BuildId[], node) => {
          if (node.buildId != null) {
            result.push(node.buildId)
          }

          return result
        }, [])
      : []

  return (
    <>
      <CloudAgentListView
        className={classNames(styles.cloudImageRow, className)}
        {...agentTypeNode}
        {...(buildIds.length > 0 ? {expandable: true, expanded, onClick} : {})}
        removable={removable}
      />
      {expanded && buildIds.length > 0 ? (
        <BuildsWrapper queuedPresent={false}>
          {buildIds.map(buildId => (
            <VisibleBuild key={buildId} buildId={buildId} />
          ))}
        </BuildsWrapper>
      ) : null}
    </>
  )
}

export default React.memo(CloudAgentRow)
